import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

/**
 * 改进版PDF导出 - 基于滚动分页的智能分页
 * @param {HTMLElement} element - 要导出的 DOM 元素
 * @param {Object} options - 配置选项
 * @param {string} options.filename - 文件名
 * @param {string} options.title - 文档标题
 * @param {boolean} options.includeCharts - 是否包含图表
 */
export const exportToPDFImproved = async (element, options = {}) => {
	const { filename = 'pdf', includeCharts = true } = options

	try {
		const pdf = new jsPDF({
			orientation: 'portrait',
			unit: 'mm',
			format: 'a4',
			putOnlyUsedFonts: true,
			compress: true,
		})

		const imgWidth = 210 // A4 宽度
		const pageHeight = 295 // A4 高度
		const pageMargin = 10 // 页面边距
		const availablePageHeight = pageHeight - pageMargin * 2 // 可用页面高度

		let currentPage = 0
		let scrollTop = 0
		let hasMoreContent = true

		if (includeCharts) {
			await renderChartsToCanvas(element)
		}

		// 设置容器样式用于分页
		element.style.minHeight = `50mm`
		element.style.height = `${availablePageHeight}mm`
		element.style.overflow = 'hidden'
		element.style.position = 'relative'

		const renderPdf = async () => {
			const canvasOptions = {
				scale: 3,
				useCORS: true,
				allowTaint: true,
				backgroundColor: '#ffffff',
				logging: false,
				width: element.scrollWidth,
				height: element.scrollHeight,
			}

			const canvas = await html2canvas(element, canvasOptions).catch(error => {
				console.error('html2canvas 渲染失败:', error)
				throw error
			})

			const imgData = canvas.toDataURL('image/png')
			const imgHeight = (canvas.height * imgWidth) / canvas.width

			// 如果是第一页，直接添加；否则添加新页
			if (currentPage > 0) {
				pdf.addPage()
			}

			pdf.addImage(imgData, 'PNG', 0, currentPage ? 10 : 0, imgWidth, imgHeight)
		}

		while (hasMoreContent) {
			const scrollTopPx = mmToPx(scrollTop)
			// 设置滚动位置（转换为像素）
			element.scrollTop = scrollTopPx

			// 检查实际滚动值，处理剩余内容不足的情况
			const actualScrollTop = element.scrollTop
			if (actualScrollTop < scrollTopPx - 1) {
				const fixHeight = scrollTopPx - actualScrollTop
				// 剩余内容不足，计算实际可用高度
				const fixHeightMm = fixHeight / (scrollTopPx / scrollTop)
				element.style.minHeight = `5mm`
				element.style.height = `${availablePageHeight - fixHeightMm}mm`
				element.scrollTop = scrollTopPx

				await renderPdf()

				hasMoreContent = false
			} else {
				// 检查当前页是否有被截断的元素
				let currentHeight = availablePageHeight
				let hasTruncatedElement = false

				do {
					// 检查是否有被截断的元素
					hasTruncatedElement = await delayCheckTruncatedElements(element)

					if (hasTruncatedElement) {
						// 减小高度
						currentHeight -= 1
						element.style.height = `${currentHeight}mm`
					}
				} while (hasTruncatedElement && currentHeight > 50) // 最小高度限制

				await renderPdf()

				// 更新滚动位置
				scrollTop += currentHeight
				currentPage++

				// 检查是否还有更多内容
				hasMoreContent = mmToPx(scrollTop) < element.scrollHeight

				// 重置高度为可用页面高度
				element.style.height = `${availablePageHeight}mm`
			}
		}

		// 6. 保存文件
		pdf.save(`${filename}.pdf`)

		element.style.minHeight = ''
		element.style.height = ''
		element.style.overflow = ''
		element.style.position = ''
		element.querySelectorAll('[truncation-checked]').forEach(el => {
			el.removeAttribute('truncation-checked')
		})

		return true
	} catch (error) {
		console.error('PDF 导出失败:', error)
		throw error
	}
}

const delayCheckTruncatedElements = element => {
	return new Promise(resolve => {
		requestAnimationFrame(() => {
			resolve(checkTruncatedElements(element))
		})
	})
}

/**
 * 检查是否有被截断的元素
 * @param {HTMLElement} element - 报告元素
 * @returns {boolean} - 是否有被截断的元素
 */
const checkTruncatedElements = element => {
	// 定义需要检查的关键元素选择器（排除容器元素）
	const criticalSelectors = ['[pdf-truncation-check-point]']

	const elementRect = element.getBoundingClientRect()
	const elementTop = elementRect.top
	const elementBottom = elementRect.bottom
	// 检查每个关键元素是否被截断
	for (const selector of criticalSelectors) {
		const elements = element.querySelectorAll(selector)
		for (const el of elements) {
			if (el.getAttribute('truncation-checked') === 'true') continue

			const rect = el.getBoundingClientRect()
			const elTop = rect.top
			const elBottom = rect.bottom

			// 元素在可见区域上方
			if (elBottom < elementTop) {
				el.setAttribute('truncation-checked', 'true')
				continue
			}
			if (elTop > elementBottom) continue // 元素在可见区域下方

			if (elTop > elementTop && elBottom < elementBottom) {
				el.setAttribute('truncation-checked', 'true')
				continue
			}

			// 如果元素顶部在可见区域内，但底部超出页面高度，说明被截断
			if (elTop < elementBottom && elBottom > elementBottom) {
				return true
			}
		}
	}

	return false
}

/**
 * 将毫米转换为像素
 * @param {number} mm - 毫米值
 * @returns {number} - 像素值
 */
const mmToPx = (() => {
	let pxPerMm = null

	return mm => {
		if (pxPerMm === null) {
			// 创建一个临时元素来计算 mm 到 px 的转换
			const div = document.createElement('div')
			div.style.width = '100mm'
			div.style.height = '0'
			div.style.position = 'absolute'
			div.style.visibility = 'hidden'
			document.body.appendChild(div)
			pxPerMm = div.offsetWidth / 100
			document.body.removeChild(div)
		}
		return mm * pxPerMm
	}
})()

/**
 * 将 ECharts 图表渲染为 canvas
 */
const renderChartsToCanvas = async element => {
	const charts = element.querySelectorAll('[data-echarts-instance]')
	const promises = []

	charts.forEach(chartElement => {
		const chartInstance = chartElement.__echarts_instance__
		if (chartInstance) {
			promises.push(
				new Promise(resolve => {
					try {
						// 获取图表数据 URL
						const dataURL = chartInstance.getDataURL({
							type: 'png',
							pixelRatio: 3,
							backgroundColor: '#ffffff',
						})

						// 创建图片元素替换图表
						const img = new Image()
						img.src = dataURL
						img.style.width = '100%'
						img.style.height = '100%'
						img.style.objectFit = 'contain'

						// 保存原始图表元素，以便恢复
						chartElement.dataset.originalChart = JSON.stringify({
							outerHTML: chartElement.outerHTML,
						})

						// 替换图表为图片
						chartElement.parentNode.replaceChild(img, chartElement)
						resolve()
					} catch (error) {
						console.warn('图表导出失败:', error)
						resolve() // 即使单个图表失败，也继续处理其他图表
					}
				}),
			)
		}
	})

	await Promise.all(promises)
}
